import Vue from 'vue';
import { getLines, getRooms, makeRoom } from '@/services/api';
import { processRoomProducts } from '@/services/room';

export default {
  namespaced: true,

  state: {
    rooms: [
      {
        id: null,
        products: [],
        line: null,
        isSaved: false,
      },
    ],
    activeRoom: null,
  },

  actions: {
    async makeRoom({ state, commit, rootGetters }, roomId) {
      const room = state.rooms.find((room) => room.id === roomId);

      if (!room) {
        throw new Error('Room not found with id ' + roomId);
      }

      const { data } = await makeRoom(room);
      Vue.set(room, 'uid', data.room.uid);
    },

    async makeAllRooms({ getters, dispatch }) {
      getters.getRooms.forEach((room) => {
        dispatch('makeRoom', room.id);
      });
    },

    async fetchUrlRooms({ dispatch, commit, rootGetters }, rooms) {
      const { data } = await getRooms(rooms);
      let processedRooms = data.rooms.map((roomBuild) => ({
        ...roomBuild,
        products: processRoomProducts(roomBuild.products, rootGetters),
      }));

      if (processedRooms.length === 1 && processedRooms[0].roomType === null) {
        commit('addNoRoomSelection', processedRooms[0]);
      } else {
        processedRooms
          .filter((room) => room.roomType !== null)
          .forEach((room) => {
            commit('addRoom', {
              ...room,
              isSaved: true,
            });
          });
      }

      const handleLines = processedRooms.map(async (roomBuild) => {
        const products = roomBuild.products.map(({ id, amount }) => ({
          id,
          amount,
        }));

        if (products.length === 0) {
          return;
        }

        const { data } = await getLines(products);

        let line = data.colorTypes.find(
          (colorType) => colorType.id === roomBuild.line
        );

        if (!line) {
          commit('setActiveRoomByUid', roomBuild.uid);
          commit('setLine', null);

          return;
        }

        line.color_name = data.colors.find(
          (color) => color.id === line.color_id
        ).name;

        commit('setActiveRoomByUid', roomBuild.uid);
        commit('setLine', line);
      });

      await Promise.all(handleLines);
    },
    updateProduct(
      { state, getters, commit, rootState, dispatch },
      { id, roomId, amount }
    ) {
      const room =
        roomId !== undefined
          ? state.rooms.find((room) => room.id === roomId)
          : getters.getActiveRoom;

      const index = room.products.findIndex((product) => product.id === id);

      if (amount > 0) {
        if (index === -1) {
          const categoryProduct = rootState['category-products'].products.find(
            (categoryProduct) => categoryProduct.id === id
          );
          if (!categoryProduct) {
            throw new Error('Category product not found with id ' + id);
          }
          commit('addProduct', { room, categoryProduct, amount });
        } else {
          commit('updateProductAmount', { room, index, amount });
        }
      } else if (index !== -1) {
        commit('removeProduct', { room, index });
      }

      dispatch('makeRoom', room.id);
    },

    resetLine({ getters }) {
      if (getters.getActiveRoom) {
        getters.getActiveRoom.line = null;
      }
    },
    updateLine({ state, rootGetters, commit }, line) {
      if (rootGetters['brand-color-selection/allRoomsSelected']) {
        state.rooms.forEach((room) => {
          room.line = line;
        });
      } else {
        commit('setLine', line);
      }
    },
  },

  mutations: {
    addProduct(state, { room, categoryProduct, amount }) {
      room.products.push({
        id: categoryProduct.id,
        categoryId: categoryProduct.category_id,
        amount,
        isCoverFrame: categoryProduct.is_cover_frame,
        coverFrameAmount: categoryProduct.cover_frame_amount,
      });
    },
    removeProduct(state, { room, index }) {
      room.products.splice(index, 1);
    },
    updateProductAmount(state, { room, index, amount }) {
      Vue.set(room.products, index, {
        ...room.products[index],
        amount,
      });
    },
    setLine(state, line) {
      const room = state.rooms.find((room) => room.id === state.activeRoom);
      Vue.set(room, 'line', line);
    },
    addRoom(state, room) {
      const newRoomId = state.rooms[state.rooms.length - 1].id + 1;

      state.rooms.push({
        id: newRoomId,
        ...room,
      });

      state.activeRoom = newRoomId;
    },
    addNoRoomSelection(state, room) {
      state.rooms = [
        {
          id: null,
          uid: room.uid,
          products: room.products,
        },
      ];
    },
    setActiveRoom(state, roomId) {
      state.activeRoom = roomId;
    },
    setActiveRoomByUid(state, roomUid) {
      const room = state.rooms.find((room) => room.uid === roomUid);

      if (!room) {
        return;
      }

      state.activeRoom = room.id;
    },
    updateRoom(state, room) {
      const roomToUpdate = state.rooms.find((r) => room.id === r.id);

      if (!roomToUpdate) {
        return;
      }

      Object.assign(roomToUpdate, room);
    },
    removeRoom(state, roomId) {
      const roomIndex = state.rooms.findIndex((room) => room.id === roomId);
      state.rooms.splice(roomIndex, 1);
      state.activeRoom =
        state.rooms.length > 0 ? state.rooms[state.rooms.length - 1].id : null;
    },
  },

  getters: {
    productIds(state, getters) {
      return getters.getActiveRoom?.products.map((product) => product.id) || [];
    },
    getActiveRoom(state) {
      return state.rooms.find((room) => room.id === state.activeRoom);
    },
    getRooms(state) {
      return state.rooms.filter((room) => room.id !== null);
    },
    getProducts(state, getters) {
      return getters.getActiveRoom?.products || [];
    },
    getProductsForRoom: (state) => (roomId) => {
      return state.rooms.find((room) => room.id === roomId)?.products || [];
    },
    getAllProducts(state) {
      const productMap = new Map();

      state.rooms.forEach((room) => {
        room.products.forEach((product) => {
          if (productMap.has(product.id)) {
            productMap.get(product.id).amount += product.amount;
          } else {
            productMap.set(product.id, { ...product });
          }
        });
      });

      return Array.from(productMap.values());
    },
    hasRooms(state) {
      return state.rooms.length > 1;
    },
    getSelectedLine(state, getters) {
      return getters.getActiveRoom?.line;
    },
    getAllSelectedLines(state, getters) {
      return getters.getRooms.map((room) => room.line);
    },
    getRoomGuids(state) {
      return state.rooms.filter((room) => room.uid).map((room) => room.uid);
    },
  },
};
